import { useOrderResourceScope } from '@/features/auth/hooks/useOrderResourceScope'
import React from 'react'
import { Navigate, useLocation, useParams } from 'react-router-dom'

interface OrderScopeGuardProps {
  children: React.ReactNode
  /** When set, overrides the `orderId` route param (e.g. order id from navigation state). */
  orderId?: string
}

function ScopeCheckLoading() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background-gray dark:bg-gray-900">
      <div className="w-full max-w-md p-8">
        <div className="rounded-base border border-border-base bg-white p-8 shadow-card dark:border-gray-700 dark:bg-gray-900">
          <div className="text-center">
            <div
              className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-brand-600 border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
              role="status"
              aria-label="Loading"
            />
            <p className="mt-2 text-sm font-medium text-text-body dark:text-gray-400">Verifying order access…</p>
          </div>
        </div>
      </div>
    </div>
  )
}

function OrderNotFound() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background-gray px-4 dark:bg-gray-900">
      <div className="w-full max-w-lg rounded-base border border-border-base bg-white p-8 text-center shadow-card dark:border-gray-700 dark:bg-gray-900">
        <h1 className="text-lg font-semibold tracking-tight text-text-heading dark:text-white">Order not found</h1>
        <p className="mt-2 text-sm font-medium text-text-body dark:text-gray-400">
          This order does not exist or you do not have access to view it.
        </p>
      </div>
    </div>
  )
}

function OrderScopeError({ message }: { message: string }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background-gray px-4 dark:bg-gray-900">
      <div className="w-full max-w-lg rounded-base border border-border-base bg-white p-8 text-center shadow-card dark:border-gray-700 dark:bg-gray-900">
        <h1 className="text-lg font-semibold tracking-tight text-text-heading dark:text-white">
          Unable to verify order
        </h1>
        <p className="mt-2 text-sm font-medium text-text-body dark:text-gray-400">{message}</p>
      </div>
    </div>
  )
}

/**
 * Blocks order deep-link routes until GET /auth/resource-scope/order/{id} returns 204.
 * Skips the probe when no order id is present (e.g. patient-only in-progress URLs).
 */
export const OrderScopeGuard: React.FC<OrderScopeGuardProps> = ({ children, orderId: orderIdProp }) => {
  const location = useLocation()
  const params = useParams<{ orderId?: string }>()
  const orderId = (orderIdProp ?? params.orderId)?.trim()
  const scope = useOrderResourceScope(orderId)

  if (!orderId) {
    return <>{children}</>
  }

  switch (scope.status) {
    case 'idle':
    case 'loading':
      return <ScopeCheckLoading />
    case 'in_scope':
      return <>{children}</>
    case 'not_found':
      return <OrderNotFound />
    case 'unauthorized':
      return <Navigate to="/login" state={{ from: location }} replace />
    case 'error':
      return <OrderScopeError message={scope.message} />
    default: {
      const _exhaustiveStatus: never = scope
      void _exhaustiveStatus
      return <ScopeCheckLoading />
    }
  }
}

export default OrderScopeGuard
